import {
  comparisonType,
  DashboardSchemaType,
  ICategoryTempData,
  ICell,
  IDataSchema, IQueryDashboardResult,
  ISeriesTempData, ISortConfig
} from "../../types";
import { IDictionary, ILimit, ISelectedNode } from "../../../../types";
import { IAxisBand, IAxisLinear, IPoint, ISeriesWithAxes } from "../../../../components/charts/types";
import { getSchema } from "../utils";
import { getPaletteColor } from "../../../../configuration/configuration";
import { cloneObject } from "../../../../system/utils";
import { formats } from "../../../../system/formats";
import { converters } from "../../../../system/converters";
import { paletteController } from "../../interruptionDashboard";

export const getSeriesName = (schema: IDataSchema, cell: ICell, row: IDictionary<any>): string | null => {
  if (cell.groups.length === 0){
    return null;
  }

  let name = formats[schema.dimensions[cell.groups[0].field].formatName](row[cell.groups[0].field]);

  for (let i = 1; i < cell.groups.length; i++) {
    const g = cell.groups[i];
    name += ` - ${formats[schema.dimensions[g.field].formatName](row[g.field])}`;
  }

  return name;
};

const sortCategories = (c1: ICategoryTempData, c2: ICategoryTempData, sort?: ISortConfig): number => {
  if (!sort) {
    return 0;
  }

  if (sort.orderBy === "VALUE") {
    return sort.orderAsc ? c1.value - c2.value : c2.value - c1.value;
  }

  const name1 = `${c1.name}`;
  const name2 = `${c2.name}`;

  return sort.orderAsc ? name1.localeCompare(name2) : name2.localeCompare(name1);
};

export const getGenericChartConfig = (
  cellData: IQueryDashboardResult,
  cell: ICell,
  _node: ISelectedNode,
  palette: IDictionary<string>,
  updatePalette: (palette: IDictionary<string>, hasChange: boolean) => void,
  schemas: Record<DashboardSchemaType, any>): [ISeriesWithAxes[], IAxisBand, IAxisLinear[]] => {
  const { result: data, limits } = cellData;
  const schema = getSchema(schemas, cell.module);
  const categories: ICategoryTempData[] = [];
  const seriesTempData: ISeriesTempData[] = [];
  const series: ISeriesWithAxes[] = [];
  const yAxis: IAxisLinear[] = [];

  let paletteChanged = false;

  const getLimits = (key: string, categoryName: string) => {
    if (limits && limits[key] && limits[key][categoryName]) {
      const record = limits[key][categoryName];
      const list: ILimit[] = [];

      if (record.minimum !== undefined) {
        list.push({ color: "red", title: "Minimum", value: record.minimum });
      }

      if (record.maximum !== undefined) {
        list.push({ color: "red", title: "Maximum", value: record.maximum });
      }

      return list.length > 0 ? list : undefined;
    }

    return undefined;
  };

  data.forEach(row => {
    const categoryName = formats[schema.dimensions[cell.dimension].formatName](row[cell.dimension])  ?? "UNDEFINED";
    let categoryData = categories.find(x => x.name === categoryName);
    if (!categoryData) {
      categoryData = {
        rawName: row[cell.dimension] ?? "UNDEFINED",
        name: categoryName,
        value: 0
      };
      categories.push(categoryData);
    }

    let seriesName = getSeriesName(schema, cell, row); // ?? "UNDEFINED";
    cell.variables.forEach( s => {
      const axisName = s.field + (s.unit ? ` - [${s.unit}]` : "");
      let axis = yAxis.find(x => x.name === axisName);
      if (!axis) {
        axis = {
          name: axisName,
          minimum: Number.MAX_VALUE,
          maximum: Number.MIN_VALUE,
          ticksCount: 3,
          adjustAxis: cell.custom && cell.custom.adjustYAxis,
          formatTick: (value) => {
            if (value === null) {
              return "---";
            }

            const doubleValue = typeof value === "number" ? value as number : (value as IPoint).y;
            // const converted =  schema.variables[s.field].converter(doubleValue, s.unit);

            return doubleValue.toFixed(s.decimals);
          }
        };

        yAxis.push(axis);
      }

      let seriesTemp: ISeriesTempData | undefined;

      seriesName = seriesName ?? s.field;
      let item = series.find(x => x.name === seriesName);
      if (!item) {
        paletteController.palette[seriesName] = true;
        if (!palette[seriesName]) {
          palette[seriesName] = getPaletteColor();
          paletteChanged = true;
        }

        seriesTemp = {
          index: series.length,
          name: seriesName,
          value: 0,
        };

        item = {
          name: seriesName,
          xAxisName: cell.dimension,
          yAxisName: axis.name,
          value: [],
          color: palette[seriesName],
          unit: s.unit ?? undefined
        };

        series.push(item);
        seriesTempData.push(seriesTemp);
      } else {
        seriesTemp = seriesTempData.find(ss => ss.name === seriesName);
      }

      const valueY = converters[schema.variables[s.field].converterName](row[s.field], s.unit); // / Math.max(1, !cell.machine && node.children ? node.children.length : 1);

      (item.value as IPoint[]).push({ x: categoryName, y: valueY, limits: getLimits(item.name, categoryName) });

      if (categoryData) {
        categoryData.value += valueY;
      }

      if (seriesTemp) {
        seriesTemp.value += valueY;
      }

      if (typeof row[s.field] === "number") {
        axis.maximum = Math.max(axis.maximum, valueY);
        axis.minimum = axis.adjustAxis ? Math.min(axis.minimum, valueY) : 0;
      }
    });
  });

  yAxis.forEach(y => {
    const diff = (y.maximum - y.minimum) * .1;
    y.maximum += diff;
    y.minimum -= diff;
  });

  const categoriesList = cell.filterBy === "DIMENSION" && cell.top > 0
    ? categories.sort((a, b) => (b.value - a.value) * (cell.orderAsc ? -1 : 1))
    : categories;


  const categoriesFiltered = cell.top === 0 || cell.filterBy !== "DIMENSION"
    ? categoriesList.sort((a, b) => a.rawName > b.rawName ? 1 : a.rawName < b.rawName ? -1 : 0)
    : categoriesList.slice(0, cell.top);

  const categoriesSorted = cell.sort && cell.sort.orderBy !== "NONE"
    ? categoriesFiltered.sort((a, b) => sortCategories(a, b, cell.sort))
    : categoriesFiltered;

  const xAxis = {
    name: cell.dimension + (cell.top > 0 ? ` - [top: ${cell.top}]` : ""),
    categories: categoriesSorted.map((c, i, a) => formats[schema.dimensions[cell.dimension].formatName](c.rawName, i, a.length)  ?? "UNDEFINED"),
    formatTick: formats[schema.dimensions[cell.dimension].formatName]
  };

  const seriesList = cell.filterBy === "SERIES" && cell.top > 0
    ? seriesTempData.sort((a, b) => (b.value - a.value) * (cell.orderAsc ? -1 : 1))
      .slice(0, cell.top)
    : seriesTempData;

  const seriesFiltered = seriesList.map(s => {
    const points: IPoint[] = [];
    const newSeries = cloneObject(series[s.index]);
    newSeries.value = points;
    const hasCategories =(series[s.index].value as IPoint[]).filter(p => xAxis.categories.find(c => c === p.x)).length > 0;

    if (hasCategories) {
      xAxis.categories.forEach(c => {
        const point = (series[s.index].value as IPoint[]).find(z => z.x === c);
        points.push({ x: c, y: point ? point.y : 0, limits: point?.limits });
      });
    }

    return hasCategories ? newSeries : null;
  });

  updatePalette(palette, paletteChanged);
  // console.log("seriesFiltered", seriesFiltered);

  return [seriesFiltered.filter(s => s !== null) as ISeriesWithAxes[], xAxis, yAxis];
};

export const processConditional = (point: IPoint, conditional: comparisonType, value: number): boolean => {

  switch (conditional) {
    case ">":
      return point.y > value;

    case ">=":
      return point.y >= value;

    case "<":
      return point.y < value;

    case "<=":
      return point.y <= value;

    case "=":
      return point.y === value;

    case "<>":
      return point.y !== value;

    default:
      return false;
  }
};